/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, ViewChildren, QueryList, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbCarousel, NgbCarouselConfig } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AdminService } from 'app/modules/admin/services/admin.service';
import { ModalCloseActionType } from 'app/modules/social/modal-create-social/modal-create-social.types';
import { ModalSocialInviteComponent } from 'app/modules/social/modal-social-invite/modal-social-invite.component';
import { ModalInviteData } from 'app/modules/social/modal-social-invite/ModalInviteData';
import { ClipboardResponseService } from 'app/modules/social/services/clipboard-response.service';
import { NONE_PLAYLIST, NONE_PLAYLIST_ID } from 'app/modules/social/services/playlist.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { newRoomSizeSettings } from 'app/modules/social/social-settings/room-size-settings';
import { SocialSettings } from 'app/modules/social/social-settings/social-settings.type';
import { actions } from 'app/modules/social/state/app';
import { selectors } from 'app/modules/social/state/home-page';
import { getInviteText } from 'app/modules/social/strings';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';
import SocialPermissionType from 'enums/SocialPermissionType';
import Paging from 'rest/Paging';

import { ScootaverseEditService } from '../../services/scootaverse-edit.service';

@Component({
  selector: 'room-settings',
  templateUrl: './room-settings.component.html',
  styleUrls: ['./room-settings.component.scss'],
  standalone: false,
})
export class RoomSettingsComponent extends Loggable implements OnInit, OnDestroy {
  public data!: ModalCreateSocialData;
  public showLoader = false;
  public socialFormGroup!: UntypedFormGroup;
  public formDidChange = false;
  public playlistOptions?: Playlist[];
  public playlistSelectedName?: string;
  public socialPermissionTypeEnum = SocialPermissionType;
  public ModalCloseActionType = ModalCloseActionType;
  public isReadOnly = true;
  public isCompanyAdmin = false;
  public savedRoomsLimitReached?: boolean;
  public maxRoomsLimit = 1;

  private geoMusicAllowed?: boolean;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private themeSelected?: SocialTheme;
  private user?: User;

  @ViewChildren('themeCarousel') public themeCarousel?: QueryList<NgbCarousel>;
  private enableNewRoomSizes: boolean | undefined;

  public constructor(
    private fb: UntypedFormBuilder,
    private videoChatService: VideoChatService,
    private _clipboardResponseService: ClipboardResponseService,
    private scootaverseEditService: ScootaverseEditService,
    private _toasterService: ToastrService,
    domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private carouselConfig: NgbCarouselConfig,
    private adminService: AdminService,
    private dialog: MatDialog,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super();
    this.carouselConfig.interval = 0;
    this.carouselConfig.wrap = false;
    this.carouselConfig.keyboard = false;
    this.carouselConfig.showNavigationIndicators = false;
    this.showLoader = true;

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        const socialId = this.route.snapshot.paramMap.get('room-id');

        if (!socialId) {
          this.error('No social id informed. Returning to home');
          void this.router.navigate([`home/scootaverses`]);
          return;
        } else {
          firstValueFrom(this.videoChatService.getSocial(socialId, false))
            .then((room) => {
              this.scootaverseEditService
                .getRoomSettings(socialId as string)
                .then((roomSocialSettings: any) => {
                  this.data = {
                    social: room,
                    action: 'edit',
                    socialSettings: roomSocialSettings,
                  };
                  this.store.dispatch(actions.LOAD_SOCIAL({ social: room }));
                  void this.loadFieldsBySocial();
                })
                .catch((err: unknown) => {
                  this.error('Error retrieving room settings data ---', err);
                  void this.router.navigate([`home/scootaverses`]);
                  this.showLoader = false;
                  return;
                });
            })
            .catch((err) => {
              this.error('Error retrieving room data ---', err);
              void this.router.navigate([`home/scootaverses`]);
              this.showLoader = false;
              return;
            });
        }
      });

    this.store
      .select(coreSelectors.getGeoMusicAllowed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowed) => (this.geoMusicAllowed = allowed));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => (this.user = user));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_new_room_sizes)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enableNewRoomSizes) => {
        this.enableNewRoomSizes = enableNewRoomSizes;
      });
  }

  private async loadFieldsBySocial() {
    this.isReadOnly = this.data.action === 'share';
    this.carouselConfig.showNavigationArrows = !(this.data.action === 'share');

    // Build Form Group

    this.socialFormGroup = this.fb.group({
      name: new UntypedFormControl({ value: this.data.social?.name, disabled: this.isReadOnly }, [Validators.required]),
      playlistId: new UntypedFormControl({
        value: this.data.social?.playlistId ?? NONE_PLAYLIST_ID,
        disabled: this.isReadOnly,
      }),
      socialPermissionType: new UntypedFormControl({
        value: this.data.social?.socialPermissionType,
        disabled: this.isReadOnly,
      }),
      themeId: new UntypedFormControl(this.data.social?.themeId),
      anyoneCanStart: this.data.social?.anyoneCanStart,
      recordMeeting:
        this.data.socialSettings?.setSettings && this.data.socialSettings?.setSettings['RECORD']
          ? this.data.socialSettings?.setSettings['RECORD']
          : false,
    });

    // Get Playlists
    await this.getPlaylists();

    // Detect Changes
    this.socialFormGroup?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.formDidChange = true));

    // - Playlist
    this.socialFormGroup
      .get('playlistId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((newPlaylistId: string) => {
        this.playlistSelectedName = this.getPlaylistNameById(newPlaylistId);
      });

    // - socialPermissionType change
    this.socialFormGroup
      .get('socialPermissionType')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value: any) => {
        this.data.social = {
          ...this.data.social,
          socialPermissionType: value,
        };

        this.showLoader = true;
        if (!this.isReadOnly) {
          this.videoChatService
            .updateSocial(this.data.social)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              next: () => (this.showLoader = false),
              error: (error: unknown) => {
                this.showLoader = false;
                this.error('Failed to update social', error);
              },
            });
        }
      });

    //disable allow join before host toggle if the room requires host admission
    if (this.data?.social?.hostAdmitted) {
      this.socialFormGroup.get('anyoneCanStart')?.disable();
    }
    this.showLoader = false;
  }

  public async ngOnInit(): Promise<void> {
    this.showLoader = true;

    // Check if is Admin of the Company
    this.isCompanyAdmin = await this.adminService.isAdminDirect();
    if (this.isCompanyAdmin == null) {
      this.isCompanyAdmin = false;
    }

    this.store
      .select(selectors.getMyRooms)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((myRooms) => this.getMaxSavedCount(myRooms.length));
    this.showLoader = false;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async getMaxSavedCount(savedRoomsCount: number): Promise<void> {
    this.maxRoomsLimit = (await this.videoChatService.getMaxSavedCount()).valueOf();
    if (this.maxRoomsLimit.valueOf() === 0 || this.data?.action === 'edit') {
      this.savedRoomsLimitReached = false;
    } else {
      this.savedRoomsLimitReached = this.maxRoomsLimit <= savedRoomsCount;
    }
  }

  public async getPlaylists(): Promise<void> {
    const paging = new Paging(0, 100);
    const playlists = (await this.videoChatService.getAllPlaylists(paging)) ?? [];

    playlists.sort((a, b) => {
      if (a.type === undefined) {
        return 1;
      }
      if (b.type === undefined) {
        return -1;
      }

      return a.type > b.type ? -1 : a.type < b.type ? 1 : 0;
    });

    this.playlistOptions = [NONE_PLAYLIST, ...playlists];
    this.playlistSelectedName = this.getPlaylistNameById(this.data?.social?.playlistId ?? NONE_PLAYLIST_ID);
  }

  public getPlaylistNameById(playlistId: string): string {
    const playlistSelectedFound = this.playlistOptions?.find((playlist) => playlist.playlistId === playlistId);
    return playlistSelectedFound?.name ?? '-';
  }

  public copyLink(): void {
    const { socialURI, user } = this.data?.social;
    assertDefined(user);
    assertDefined(socialURI);
    const name = formatUserName(user);

    const invite = getInviteText(name, socialURI, false);

    this._clipboardResponseService._clipboardService.copyFromContent(invite);
    this._toasterService.success('Copied to clipboard', 'Success');
  }

  // ngb-carousel slide listener
  public onSlide(theme: SocialTheme): void {
    this.themeSelected = theme;
    this.socialFormGroup.get('themeId')?.setValue(theme.socialThemeId);
  }

  private upgradeRoomSize(size: number): number {
    const newSetting = newRoomSizeSettings.filter((newValue) => newValue.value.size === size)[0];
    return newSetting.value.maxParticipants;
  }

  public isMaxParticipantsLegal(maxParticipants: number, startingWidth?: number): boolean {
    const setting = newRoomSizeSettings.filter((newValue) => newValue.value.size === startingWidth)[0];
    return maxParticipants <= setting.value.maxParticipants;
  }

  private getRoomData(recurring?: boolean): Social {
    const { social } = this.data;
    const playlistId = this.socialFormGroup.get('playlistId')?.value ?? NONE_PLAYLIST_ID;

    const socialCopy = structuredClone(social);
    if (
      this.enableNewRoomSizes &&
      socialCopy.maxParticipants &&
      socialCopy.startingWidth &&
      !this.isMaxParticipantsLegal(socialCopy.maxParticipants, socialCopy.startingWidth)
    ) {
      socialCopy.maxParticipants = this.upgradeRoomSize(socialCopy.startingWidth);
    }

    return {
      ...socialCopy,
      name: this.socialFormGroup.get('name')?.value,
      socialPermissionType: this.socialFormGroup.get('socialPermissionType')?.value,
      themeId: this.socialFormGroup.get('themeId')?.value,
      playlistId,
      recurring,
      anyoneCanStart: this.socialFormGroup.get('anyoneCanStart')?.value,
    };
  }

  public updateSocial(callbackAction: ModalCloseActionType, recurring?: boolean): void {
    this.showLoader = true;
    this.info(callbackAction, recurring);
    if (this.socialFormGroup.get('name')?.value) {
      const roomToUpdate = this.getRoomData(recurring);
      const setSettings = {
        RECORD: this.socialFormGroup.get('recordMeeting')?.value,
      };
      this.scootaverseEditService
        .saveRoom({
          room: roomToUpdate,
          action: callbackAction,
          socialSettings: { settings: this.data?.socialSettings?.settings, setSettings: setSettings },
        })
        .then(() => {
          this.showLoader = false;
        })
        .catch((err) => {
          this.error('Error saving room information', err);
          this.showLoader = false;
        });
    }
  }

  public saveAsRecurring(): void {
    if (!this.user) {
      this.error('saveAsRecurring: User is not logged in');
      return;
    }
    const { user } = this;

    this.updateSocial(ModalCloseActionType.SAVE, true);
    const data: ModalInviteData = {
      socialURI: this.data?.social?.socialURI ?? '',
      text: 'Paste the link to your calendar event or share directly with others.',
      modalTitle: 'Share the Link to this Room',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
    };
    this.dialog.open(ModalSocialInviteComponent, {
      panelClass: 'no-padding-social-modal',
      backdropClass: 'social-page-backdrop',
      data,
    });
  }

  public navigateToAdvancedSettings(): void {
    this.updateSocial(ModalCloseActionType.SETTINGS);
  }

  public closeDetail(): void {
    void this.router.navigate([`home/scootaverses`]);
  }
}

export interface ModalCreateSocialData {
  action: 'add' | 'share' | 'edit';
  social: Social;
  socialSettings?: SocialSettings;
}
